import {Button, Flex, Typography} from "antd";
import {UploadFilesModal} from "../../../../Common/UploadFileModal/UploadFilesModal";
import {useState} from "react";
import {AttachmentType, PracticeFileRow, UploadAttachment} from "../../../../../models/Practice/PracticeFIle";
import {PracticeDetail, PracticeProgress, ProceedNextRequest} from "../../../../../models/Practice/PracticeDetail";
import {PracticeProgressStatus} from "../../../../../models/Practice/Practice";
import {AuthService} from "../../../../../services/AuthService";
import {PracticesHttpService} from "../../../../../services/Http/PracticesHttpService";
import {NotificationService} from "../../../../../services/NotificationService";
import {useParams} from "react-router-dom";
import {InsuranceProviderIntegrationCodes} from "../../../../../models/InsuranceProvider";
import {useClaimStatus, claimClosed} from "../../../ClaimStatusProvider";
import {Utils} from "../../../../../services/Utils";
import dayjs from "dayjs";

export interface UploadFilesLiquidationProps {
    practiceDetail: PracticeDetail;
    files: Array<PracticeFileRow>;
    loadFiles: () => void;
    disableButtons:boolean;
    progress: PracticeProgress[] | undefined,
    status: PracticeProgressStatus
    reloadDetail: () => Promise<void>
}

export function UploadFilesLiquidation(props: UploadFilesLiquidationProps){
    const { Text} = Typography;
    const { id } = useParams();

    const [isModalOpen, setModalOpen] = useState<boolean>(false)
    const [dataToSend, setDataToSend] = useState<UploadAttachment>();
    const [isInvoice, setIsInvoice] = useState<boolean>(false);

    const claimStatus = useClaimStatus();

    const onClose= function() {
        if (isModalOpen)
            setModalOpen(false)
        props.loadFiles();
    }

    const currentIntegrationCode = props.practiceDetail.insuranceProvider.integrationCode;
    const lensComplainceRequired = ["BLUEASSISTANCE", "INSALUTE", InsuranceProviderIntegrationCodes.AXA.toString()].includes(currentIntegrationCode);
    const signedPicRequired = ["BLUEASSISTANCE", "INSALUTE", "CASPIE", "GENERALI", InsuranceProviderIntegrationCodes.AXA.toString()].includes(currentIntegrationCode);
    const declarationRequired = ["POSTE","POSTEWELFARE"].includes(currentIntegrationCode);

    const disableButtons = ()=> {
        return claimClosed(claimStatus) || props.disableButtons || props.status === PracticeProgressStatus.PIC_NON_AUTORIZZATA;
    }

    const uploadAttachmentLensComplaince : UploadAttachment ={
        filename: "ConformitaLenti.png",
        attachmentType: AttachmentType.LENS_COMPLAINCE
    }

    const uploadAttachmentInvoice : UploadAttachment ={
        filename: "fattura.png",
        attachmentType: AttachmentType.INVOICE
    }

    const uploadAttachmentSignedPic : UploadAttachment ={
        filename: "picFirmata.png",
        attachmentType: AttachmentType.SIGNED_PIC
    }

    const uploadAttachmentDeclaration : UploadAttachment ={
        filename: "dichiarazioneFirmata.png",
        attachmentType: AttachmentType.DECLARATION
    }

    const uploadAttachmentCreditNote : UploadAttachment ={
        filename: "notaDiCredito.png",
        attachmentType: AttachmentType.CREDIT_NOTE
    }

    const openModal = (docLoaded: boolean, dataToSend: UploadAttachment, isInvoice: boolean) => {
        if (docLoaded) return;

        setDataToSend(dataToSend);
        setModalOpen(true);
        setIsInvoice(isInvoice);
    }

    const invoiceLoaded = props.files.find(file => file.type == AttachmentType.INVOICE || file.type == AttachmentType.DRAFT_INVOICE) != undefined;
    const lensComplainceLoaded = props.files.find(file => file.type == AttachmentType.LENS_COMPLAINCE) != undefined;
    const signedPicLoaded = props.files.find(file => file.type == AttachmentType.SIGNED_PIC) != undefined;
    const declarationLoaded = props.files.find(file => file.type == AttachmentType.DECLARATION) != undefined;
    const creditNoteLoaded = props.files.find(file => file.type == AttachmentType.CREDIT_NOTE) != undefined;

    const isLastDocDaInviare = Utils.getLastProgressData(props.progress)?.name === PracticeProgressStatus.DOCUMENTAZIONE_DA_INVIARE;
    const isAxaProvider = props.practiceDetail.insuranceProvider.integrationCode === InsuranceProviderIntegrationCodes.AXA.toString();
    const isIntesaProvider = props.practiceDetail.insuranceProvider.integrationCode === InsuranceProviderIntegrationCodes.ISP.toString();
    const shouldConfirm = ([
        PracticeProgressStatus.PIC_AUTORIZZATA,
        PracticeProgressStatus.ERRORE_IN_FATTURA
    ].includes(props.status) || (isAxaProvider && isLastDocDaInviare)) && !isIntesaProvider;

    function shouldUploadCreditNoteIntesa()  {
        if (currentIntegrationCode !== InsuranceProviderIntegrationCodes.ISP.valueOf() ||
            props.practiceDetail.picMetadata.orderData?.fieldData?.pagamentoMetodo === "Bonifico") {
            return false;
        }
        const invoiceFile = props.files.find(file => file.type === AttachmentType.INVOICE);
        if (invoiceFile && invoiceFile.metadata && invoiceFile.metadata.date) {
            const invoiceDate = dayjs(invoiceFile.metadata.date, "YYYYMMDD");
            const deadlineDate = invoiceDate.add(14, 'days').endOf('day'); // il 15-esimo giorno dopo non è più possibile caricare la nota di credito
            console.info("deadlineDate Nota di Credito Intesa: ", deadlineDate);
            return dayjs().isBefore(deadlineDate);
        }
        return true;
    }

    const enableUploadAttachmentCreditNoteButtons = (status: string)=> {
        return !claimClosed(claimStatus) && !props.disableButtons
            && (props.status === PracticeProgressStatus.FATTURA_INVIATA || props.status === PracticeProgressStatus.FATTURA_DA_INVIARE)
            && !creditNoteLoaded;
    }

    const canConfirm = (
        invoiceLoaded &&
        (!lensComplainceRequired || lensComplainceLoaded) &&
        (!signedPicRequired || signedPicLoaded) &&
        (!declarationRequired || declarationLoaded)
    )

    const executeAfterUpload = async function() {
        if (isIntesaProvider) {
            await onDocConfimed();
        }
    }

    const onDocConfimed = async () => {
        let operator = AuthService.getOperatorOnline();
        const eventToSend = (dataToSend?.attachmentType === AttachmentType.CREDIT_NOTE && isIntesaProvider)
            ? PracticeProgressStatus.RECESSO_DA_INVIARE : PracticeProgressStatus.FATTURA_DA_INVIARE;
        const body: ProceedNextRequest = { metadata: {operator: operator!.name }, event: eventToSend}
        try {
            await PracticesHttpService.saveProceedNext(id ?? "", body);
            await props.reloadDetail();
        } catch (_) {
            NotificationService.getInstance().openNotificationError("Operazione non riuscita")
        }
    }

    return<>
        <div>
            <Text strong>Documenti obbligatori per liquidazione </Text>
            <Flex gap={"small"} className={"margin-y-10px"}>
                <Button disabled={disableButtons()} type={"primary"} className={invoiceLoaded ? "luxottica-button-success" : ""} onClick={()=>{openModal(invoiceLoaded, uploadAttachmentInvoice, true)}}>Fattura</Button>
                { lensComplainceRequired && <Button disabled={disableButtons()} type={"primary"} className={lensComplainceLoaded ? "luxottica-button-success" : ""} onClick={()=>{openModal(lensComplainceLoaded, uploadAttachmentLensComplaince, false)}}>Conformità lenti</Button> }
                { signedPicRequired && <Button disabled={disableButtons()} type={"primary"} className={signedPicLoaded ? "luxottica-button-success" : ""} onClick={()=>{openModal(signedPicLoaded, uploadAttachmentSignedPic, false)}}>PIC firmata</Button> }
                { declarationRequired && <Button disabled={disableButtons()} type={"primary"} className={declarationLoaded ? "luxottica-button-success" : ""} onClick={()=>{openModal(declarationLoaded, uploadAttachmentDeclaration, false)}}>PIC firmata</Button> }
                { dataToSend && <UploadFilesModal practiceDetail={props.practiceDetail} attachmentData={dataToSend} isInvoice={isInvoice} isModalOpen={isModalOpen} onClose={onClose} onAfterUpload={executeAfterUpload}></UploadFilesModal> }
                { shouldConfirm && <Button disabled={claimClosed(claimStatus) || !canConfirm} type={"primary"} onClick={onDocConfimed}>Conferma doc.</Button>}
                { shouldUploadCreditNoteIntesa() && <Button disabled={!enableUploadAttachmentCreditNoteButtons(claimStatus)} type={"primary"} className="" onClick={()=>{openModal(false, uploadAttachmentCreditNote, false)}}>Nota di credito</Button>}
            </Flex>
        </div>

    </>
}